import React from 'react'
import { Modal } from '../../../components/Modal/Modal'
import { Button } from '../../../components/Button/Button'
import './MeetingTypeUpdatesModal.scss'

const MIGRATION_DETAILS = [
  {
    oldName: 'General Meeting Summary',
    newName: 'Internal Meeting Summary',
    description: (
      <>
        <p className='meeting-type-updates-modal__card-text'>Your internal meetings now highlight what matters first.</p>
        <p className='meeting-type-updates-modal__card-text'>
          A <strong>Decisions &amp; Insights table</strong> surfaces key outcomes at the top. Meeting Purpose clearly shows decisions and next steps. Notes are
          written in structured prose and remove irrelevant placeholders.
        </p>
        <p className='meeting-type-updates-modal__card-text mb-0'>This makes internal alignment faster and summaries quicker to act on.</p>
      </>
    ),
  },
  {
    oldName: 'Client Meeting',
    newName: 'General Client Check-in',
    description: (
      <>
        <p className='meeting-type-updates-modal__card-text'>Your client check-ins are now more structured and faster to review.</p>
        <p className='meeting-type-updates-modal__card-text'>
          Each summary starts with a <strong>Client Profile Snapshot</strong> for quick context. Financial discussions are captured in structured free-form
          text. New sections for <strong>Beneficiary Updates</strong> and <strong>Client Personal Updates</strong> keep important details organized.
        </p>
        <p className='meeting-type-updates-modal__card-text mb-0'>This helps you stay consistent and quickly reorient between meetings.</p>
      </>
    ),
  },
  {
    oldName: 'Plan Review',
    newName: 'Plan Proposal',
    description: (
      <>
        <p className='meeting-type-updates-modal__card-text'>Your proposal meetings now provide clearer direction and stronger outputs.</p>
        <p className='meeting-type-updates-modal__card-text'>
          You immediately see <strong>Outcome and Next Steps</strong>, along with a <strong>Household Summary</strong> and structured{' '}
          <strong>Financial Summary</strong>.
        </p>
        <p className='meeting-type-updates-modal__card-text mb-0'>
          Discussion notes include key data and advisor talking points. Impact areas (portfolio, cash flow, tax, estate) turn notes into structured advisory
          analysis.
        </p>
      </>
    ),
  },
] as const

export interface MeetingTypeUpdatesModalProps {
  show: boolean
  onHide: () => void
  onReviewChanges: () => void
}

export const MeetingTypeUpdatesModal: React.FC<MeetingTypeUpdatesModalProps> = ({ show, onHide, onReviewChanges }) => {
  const body = (
    <div className='meeting-type-updates-modal__content'>
      <div className='meeting-type-updates-modal__section'>
        <div className='meeting-type-updates-modal__section-bar' />
        <div className='meeting-type-updates-modal__section-text'>
          <h4 className='meeting-type-updates-modal__section-title'>{`What's changing`}</h4>
          <p className='meeting-type-updates-modal__section-subtitle mb-0'>
            These updates make your meeting summaries easier to scan, more actionable, and better aligned with how you run meetings.
          </p>
        </div>
      </div>

      <div className='meeting-type-updates-modal__cards'>
        {MIGRATION_DETAILS.map((migration) => (
          <div key={migration.oldName} className='meeting-type-updates-modal__card'>
            <div className='meeting-type-updates-modal__card-header'>
              <div className='meeting-type-updates-modal__card-from'>
                {migration.oldName}
                <i className='bi bi-arrow-right' />
              </div>
              <div className='meeting-type-updates-modal__card-to'>{migration.newName}</div>
            </div>
            <div className='meeting-type-updates-modal__card-body'>{migration.description}</div>
          </div>
        ))}
      </div>

      <div className='meeting-type-updates-modal__section'>
        <div className='meeting-type-updates-modal__section-bar' />
        <div className='meeting-type-updates-modal__section-text'>
          <h4 className='meeting-type-updates-modal__section-title'>What this means</h4>
          <p className='meeting-type-updates-modal__section-subtitle mb-0'>
            Spend less time reviewing notes. Understand what happened, what matters, and what to do next — at a glance.
          </p>
        </div>
      </div>

      <Button variant='primaryLinkButton' className='button-medium meeting-type-updates-modal__review-link' onClick={onReviewChanges}>
        Review changes in detail
        <i className='bi bi-chevron-right' />
      </Button>
    </div>
  )

  return (
    <Modal
      className='meeting-type-updates-modal'
      show={show}
      onHide={onHide}
      header={<span className='meeting-type-updates-modal__title'>Meeting type updates</span>}
      body={body}
      centered
    />
  )
}
